import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import BucketChip from '../components/BucketChip';
import HourStatusRow from '../components/HourStatusRow';
import CommercialCells, { COMMERCIAL_LABELS } from '../components/CommercialCells';
import { EMPTY, PANEL, TD, TH } from '../components/tableStyles';
import { useReport } from '../ReportContext';
import { adaptHourlyOrders } from '../adapt';
import { num, ratio } from '../format';
import { hourRange, useHourly } from '../hourly';
import { attributionTotals, totals, type Totals } from '../stats';
import { BUCKETS, WORK_BUCKETS } from '../theme';
import type { BucketKey, OpenDrill, OrderBucketKey, OrdersView, WorkRow } from '../types';

interface Props { rows: WorkRow[]; openDrill: OpenDrill }

interface Group {
  key: string; label: React.ReactNode; name: string; t: Totals;
  bucket: OrderBucketKey | 'all'; basis?: 'agent_tag' | 'discount_code';
  /** Distinct customers worked in this bucket, all agents together. Blank where work is not split that way. */
  customers: number | null;
}

const VIEWS: { key: OrdersView; label: string; hint: string }[] = [
  { key: 'bucket', label: 'By bucket', hint: 'Which workstream the order is tagged to (Bucket Tag, taken from the Shopify order).' },
  { key: 'attribution', label: 'By attribution source', hint: 'Agent Tag or agent coupon.' },
];

function buildGroups(view: OrdersView, rows: WorkRow[], byBucket: Record<string, number>): Group[] {
  if (view === 'attribution') {
    return [
      { key: 't', label: 'Agent Tag', name: 'Agent Tag', t: attributionTotals(rows, 'tag'), bucket: 'all', basis: 'agent_tag', customers: null },
      { key: 'c', label: 'Agent coupon (no valid tag)', name: 'Agent coupon', t: attributionTotals(rows, 'coupon'), bucket: 'all', basis: 'discount_code', customers: null },
    ];
  }
  const keys = Array.from(new Set(rows.map(r => r.bucket)))
    .sort((a, b) => (a === 'none' ? 99 : WORK_BUCKETS.indexOf(a as BucketKey)) - (b === 'none' ? 99 : WORK_BUCKETS.indexOf(b as BucketKey)));
  return keys.map(b => ({
    key: b, label: <BucketChip bucket={b} />, name: b === 'none' ? 'No open work' : BUCKETS[b as BucketKey].label,
    t: totals(rows.filter(r => r.bucket === b)), bucket: b,
    // Exact distinct count from the server; the sum over agents (sample mode) counts a shared customer twice.
    customers: byBucket[b] ?? totals(rows.filter(r => r.bucket === b)).uniqueCustomers,
  }));
}

const OrdersRevenueTab: React.FC<Props> = ({ rows, openDrill }) => {
  const [view, setView] = useState<OrdersView>('bucket');
  const t = useMemo(() => totals(rows), [rows]);
  const { customersByBucket } = useReport();
  const groups = useMemo(() => buildGroups(view, rows, customersByBucket), [view, rows, customersByBucket]);
  // Hour-of-day rows under each bucket (hour the order was placed), fetched the first time one is opened.
  const hourly = useHourly('orders', adaptHourlyOrders);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (k: string) => { setOpen(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; }); hourly.load(); };

  if (t.orders === 0) return <div className={EMPTY}>No orders for this selection.</div>;

  const drillFor = (g: Group) => (status: Parameters<NonNullable<React.ComponentProps<typeof CommercialCells>['drill']>>[0], what: string, total: number) =>
    openDrill({ title: `${g.name} — ${what}`, kind: 'orders', agent: 'all', bucket: g.bucket, total, orderStatus: status ?? 'all', basis: g.basis });

  return (
    <div className="space-y-5">
      <div className={PANEL}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-700">
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Order views">
            {VIEWS.map(v => (
              <button key={v.key} type="button" role="tab" aria-selected={view === v.key} onClick={() => setView(v.key)}
                className={`rounded-full px-3 py-1 text-sm ${view === v.key
                  ? 'bg-sky-100 text-sky-800 dark:bg-sky-400/20 dark:text-sky-100'
                  : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700'}`}>{v.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {view === 'bucket' && hourly.available && (
              <div className="flex gap-2 text-sm">
                <button type="button" onClick={() => { setOpen(new Set(groups.map(g => g.key))); hourly.load(); }} className="rounded-lg px-2.5 py-1 text-sky-700 hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-400/10">Expand all hours</button>
                <button type="button" onClick={() => setOpen(new Set())} className="rounded-lg px-2.5 py-1 text-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">Collapse all</button>
              </div>
            )}
            <p className="text-xs text-slate-600">{VIEWS.find(v => v.key === view)?.hint}</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-brandColor">
                <th className={`${TH} !text-white text-left whitespace-nowrap`}>{VIEWS.find(v => v.key === view)?.label.replace('By ', '')}</th>
                <th className={`${TH} !text-white text-right whitespace-nowrap`}>Unique customers</th>
                {COMMERCIAL_LABELS.map(h => <th key={h} className={`${TH} !text-white text-right whitespace-nowrap`}>{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-700/60">
              {groups.map(g => {
                const canOpen = view === 'bucket' && hourly.available && g.bucket !== 'all';
                const isOpen = canOpen && open.has(g.key);
                const hours = hourly.data?.[g.key] ?? [];
                return (
                  <React.Fragment key={g.key}>
                    <tr>
                      <td className={`${TD} whitespace-nowrap`}>
                        {canOpen ? (
                          <button type="button" onClick={() => toggle(g.key)} aria-expanded={isOpen} title="Hourly breakdown" className="flex items-center gap-1.5">
                            {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-600" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-600" />}
                            {g.label}
                          </button>
                        ) : g.label}
                      </td>
                      <td className={`${TD} text-right tabular-nums`}>{g.customers === null ? '—' : num(g.customers)}</td>
                      <CommercialCells t={g.customers === null ? g.t : { ...g.t, convPct: ratio(g.t.orders, g.customers) }} drill={drillFor(g)} />
                    </tr>
                    {isOpen && <HourStatusRow state={hourly} colSpan={2 + COMMERCIAL_LABELS.length} hasRows={hours.length > 0} />}
                    {isOpen && hours.map(({ hour, row: r }) => (
                      <tr key={`${g.key}-${hour}`} className="border-t border-slate-50 bg-slate-50/40 dark:border-slate-700/50 dark:bg-slate-800/40">
                        <td className={`${TD} whitespace-nowrap pl-9`}>{hourRange(hour)}</td>
                        <td className={`${TD} text-right tabular-nums`}>—</td>
                        <CommercialCells t={totals([r])} drill={(status, what, total) =>
                          openDrill({ title: `${g.name} · ${hourRange(hour)} — ${what}`, kind: 'orders', agent: 'all', bucket: g.bucket, total, orderStatus: status ?? 'all', hour })} />
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot className="border-t border-slate-200 bg-emerald-50/60 font-semibold dark:border-slate-600 dark:bg-emerald-400/5">
              <tr>
                <td className={`${TD} whitespace-nowrap`}>Total (each order counted once)</td>
                <td className={`${TD} text-right tabular-nums`}>—</td>
                <CommercialCells t={t} drill={(status, what, total) =>
                  openDrill({ title: `All orders — ${what}`, kind: 'orders', agent: 'all', bucket: 'all', total, orderStatus: status ?? 'all' })} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrdersRevenueTab;
